import currency from 'currency.js'
import { differenceInCalendarDays } from 'date-fns'

export const maxCampaignDurationDays = 28
export const defaultCityRadiusKm = 50

export const defaultCampaignMinBudget = 10000
export const defaultCampaignMaxBudget = 300000
export const defaultCampaignBudget = 50000

export const absoluteMinBudgetEuros = 10
export const dailyBudgetMultiplier = 2

export const recommendedDailyBudgetCents = 4000

/**
 * Campaign duration in inclusive calendar days, matching the backend's
 * `diffInDays + 1`. Counting calendar days rather than elapsed milliseconds
 * keeps the number stable across daylight-saving transitions and ignores the
 * time of day (the date picker sets the end date to 23:59:59.999).
 */
export const calculateCampaignDurationInDays = (startDate: Date, endDate: Date): number => {
	return Math.max(1, differenceInCalendarDays(endDate, startDate) + 1)
}

/** Black Friday to Christmas. The dates are repeated in the `review.peakSeasonTitle` copy. */
export const overlapsPeakAdSeason = (startDate: Date, endDate: Date): boolean => {
	for (let year = startDate.getFullYear(); year <= endDate.getFullYear(); year++) {
		const seasonStart = new Date(year, 10, 16)
		const seasonEnd = new Date(year, 11, 26, 23, 59, 59, 999)
		if (startDate <= seasonEnd && endDate >= seasonStart) {
			return true
		}
	}
	return false
}

/**
 * Calculate minimum budget based on campaign duration
 *
 * Formula: budgetMin = max(10, 2 × durationInDays) euros
 *
 * @param durationInDays - Campaign duration in days
 * @param serviceFeePercent - Service fee percentage (e.g., 50 for 50%)
 * @returns Minimum budget in cents
 */
export const calculateMinBudgetByDuration = (durationInDays: number, serviceFeePercent: number): number => {
	const minByDuration = dailyBudgetMultiplier * durationInDays
	const minBudgetEuros = Math.max(absoluteMinBudgetEuros, minByDuration)
	return eurosToCents(minBudgetEuros / ((100 - serviceFeePercent) / 100))
}

/**
 * Convert euros to cents
 * @param euros - Amount in euros
 * @returns Amount in cents
 */
export const eurosToCents = (euros: number): number => {
	if (!Number.isFinite(euros)) {
		throw new Error(`Invalid euros value: ${euros}. Must be a finite number.`)
	}

	return currency(euros).intValue
}

/**
 * Convert cents to euros
 * @param cents - Amount in cents
 * @returns Amount in euros
 */
export const centsToEuros = (cents: number): number => {
	return currency(Math.round(cents), { fromCents: true }).value
}
